import { AsYouType, getExampleNumber } from 'libphonenumber-js/min';
import { Box, InputAdornment, MenuItem, Select } from '@mui/material';
import React, { useRef, useState } from 'react';
import { getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js/max';

import PropTypes from 'prop-types';
import TextField from './TextField';
import examples from 'libphonenumber-js/mobile/examples';
import { jurisdictionOptions } from '../../helpers/Jurisdiction';
import useBoundCallback from '../../helpers/Hooks/useBoundCallback';
import { useThunkReducer } from '../../helpers/Hooks';
import { withFormikField } from './FormikFields';

// Specific country sorting for phone number input
const PHONE_COUNTRY_SORT = {
    US: -1,
};

/**
 * @typedef PhoneCountryOption
 * @property {number} sort - The sort order of the country option
 * @property {string} country - The country code (e.g., "US")
 * @property {string} callingCode - The country calling code (e.g., "+1")
 * @property {string} label - The display label for the country (e.g., "United States")
 * @property {string} flagSrc - The URL to the country's flag image
 */
/**
 * Create country options with calling codes for phone number input
 * @type {Array<PhoneCountryOption>}
 */
const PHONE_COUNTRY_OPTIONS = jurisdictionOptions
    .map(({ country, label }) => {
        if (!country) {
            return null;
        }
        try {
            const callingCode = getCountryCallingCode(
                /** @type {import('libphonenumber-js').CountryCode} */ (country.toUpperCase())
            );
            return {
                sort: PHONE_COUNTRY_SORT[country.toUpperCase()] || 0,
                country: country.toUpperCase(),
                callingCode,
                label,
                flagSrc: `/assets/images/flags/${country.toLowerCase()}.png`,
            };
        } catch {
            return null;
        }
    })
    .filter(Boolean)
    .reduce((acc, current) => {
        // Remove duplicates based on country code
        if (!acc.find(item => item.country === current.country)) {
            acc.push(current);
        }
        return acc;
    }, [])
    .sort((a, b) => a.label.localeCompare(b.label));

const numberInputStyles = {
    '& > .MuiOutlinedInput-notchedOutline': {
        overflow: 'visible',
    },
    '& > .MuiOutlinedInput-notchedOutline::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: '7rem',
        right: 0,
        bottom: 0,
        height: 0,
        borderRadius: 0,
        transitionDuration: '200ms',
        transitionProperty: 'height',
        backgroundColor: theme => theme.palette.grey[300],
    },
    '& > input:focus-visible ~ .MuiOutlinedInput-notchedOutline::after': {
        height: '4px',
    },
    '& > input:focus ~ .MuiOutlinedInput-notchedOutline::after': {
        height: '4px',
    },
    '.MuiFormControl-root:hover & > .MuiOutlinedInput-notchedOutline::after': {
        backgroundColor: theme => theme.palette.text.primary,
    },
    '.MuiFormControl-root &.Mui-focused > .MuiOutlinedInput-notchedOutline::after': {
        backgroundColor: theme => theme.palette.primary.main,
    },
    '&.Mui-error > .MuiOutlinedInput-notchedOutline::after': {
        backgroundColor: theme => `${theme.palette.error.main} !important`,
    },
};

const countrySelectStyles = {
    flex: '0 0 7rem', // 2rem (Flag Icon) + (5 characters `+_123` * 1rem) = 7rem
    lineHeight: '2.25rem',
    zIndex: 1,
    borderRadius: 0,
    boxSizing: 'content-box',
    borderRight: theme => `1px solid ${theme.palette.grey[400]}`,
    marginRight: '1px',
    '.Mui-disabled &': {
        borderRightColor: theme => `${theme.palette.grey[400]} !important`,
    },
    '& .MuiSelect-select': {
        padding: theme => theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '0.875rem',
        minWidth: 0, // Allow text to wrap/truncate
        overflow: 'hidden',
    },
    '& .MuiSelect-select::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: 0,
        bottom: 0,
        right: 0,
        height: 0,
        borderRadius: '0 0 0 0.25em',
        transitionDuration: '200ms',
        transitionProperty: 'height',
        backgroundColor: theme => theme.palette.grey[400],
    },
    '&.Mui-focused .MuiSelect-select::after': {
        height: '4px',
    },
    '.MuiFormControl-root:hover &': {
        borderRightColor: theme => theme.palette.text.primary,
    },
    '.MuiFormControl-root:hover .Mui-focused &': {
        borderRightColor: theme => theme.palette.primary.main,
    },
    '.MuiFormControl-root:hover & .MuiSelect-select::after': {
        backgroundColor: theme => theme.palette.text.primary,
    },
    '.MuiFormControl-root .Mui-focused & .MuiSelect-select::after': {
        backgroundColor: theme => theme.palette.primary.main,
    },
    '.Mui-error &': {
        borderRightColor: theme => `${theme.palette.error.main} !important`,
    },
    '.Mui-error & .MuiSelect-select::after': {
        backgroundColor: theme => `${theme.palette.error.main} !important`,
    },
    '.Mui-focused &': {
        borderRightWidth: '2px',
        marginRight: 0,
        borderRightColor: theme => theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
};

const getNationalNumber = (countryCode, value) => {
    if (!value) {
        return '';
    }
    try {
        const asYouType = new AsYouType(countryCode);
        asYouType.input(value);
        return asYouType.getNationalNumber() || '';
    } catch {
        return value;
    }
};

const formatPhoneNumber = (countryCode, value) => {
    if (!value) {
        return '';
    }
    try {
        const asYouType = new AsYouType(countryCode);
        return asYouType.input(getNationalNumber(countryCode, value));
    } catch {
        return value;
    }
};

const countDigitsBefore = (text, position) => {
    return text.slice(0, position).replace(/\D/g, '').length;
};

const findCursorPosition = (formattedText, targetDigitPosition) => {
    let position = 0;
    let numericCount = 0;

    for (let i = 0; i < formattedText.length; i++) {
        if (/\d/.test(formattedText[i])) {
            numericCount++;
        }
        position++;
        if (numericCount >= targetDigitPosition) {
            break;
        }
    }
    return position;
};

const makePlaceholder = countryCode => {
    try {
        const exampleNumber = getExampleNumber(countryCode, examples);
        if (exampleNumber?.nationalNumber) {
            const asYouType = new AsYouType(countryCode);
            asYouType.input(exampleNumber.nationalNumber);
            return asYouType.getTemplate();
        }
    } catch {
        // Do nothing
    }
    return '';
};

const DEFAULT_COUNTRY = 'US';
const initialState = {
    country: DEFAULT_COUNTRY,
    formattedPhoneNumber: '',
    nationalNumber: '',
    placeholder: '',
    value: '',
};

const initState = props => {
    // Parse initial value into calling code and national number
    const initialValue = `${props.value || props.defaultValue || ''}`;
    let matchingCountry, nationalNumber;
    try {
        const parsedNumber = parsePhoneNumber(initialValue);
        nationalNumber = parsedNumber.nationalNumber;
        matchingCountry = PHONE_COUNTRY_OPTIONS.filter(
            ({ callingCode }) => parsedNumber.countryCallingCode === callingCode
        ).sort(({ sort: a }, { sort: b }) => a - b)[0];
    } catch (e) {
        matchingCountry = PHONE_COUNTRY_OPTIONS.filter(country =>
            initialValue.startsWith(`+${country.callingCode}`)
        ).sort(({ sort: a }, { sort: b }) => a - b)[0];

        if (matchingCountry) {
            nationalNumber = initialValue.slice(matchingCountry.callingCode.length);
        }
    }

    if (!matchingCountry) {
        matchingCountry =
            PHONE_COUNTRY_OPTIONS.find(
                ({ country }) => country === initialState.country.toUpperCase()
            ) || PHONE_COUNTRY_OPTIONS.find(({ country }) => country === DEFAULT_COUNTRY);
        nationalNumber = initialValue
            // eslint-disable-next-line security/detect-non-literal-regexp
            .replace(new RegExp(`^\\+${matchingCountry.callingCode}`), '')
            .replace(/[^\d]/g, '');
    }

    return {
        country: matchingCountry.country,
        formattedPhoneNumber: formatPhoneNumber(matchingCountry.country, nationalNumber),
        nationalNumber,
        placeholder: makePlaceholder(matchingCountry.country),
        value: `+${matchingCountry.callingCode}${nationalNumber}`,
    };
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            return {
                ...state,
                ...initState(action.payload),
            };
        }
        case 'SET_COUNTRY': {
            const { nationalNumber } = state;
            const country = action.payload;
            const newFormattedNumber = formatPhoneNumber(country, nationalNumber);
            return {
                ...state,
                country,
                formattedPhoneNumber: newFormattedNumber,
                placeholder: country ? makePlaceholder(country) : '',
                value: `+${getCountryCallingCode(country)}${nationalNumber}`,
            };
        }
        case 'SET_PHONE_NUMBER': {
            const { country } = state;
            const phoneNumber = action.payload;
            const newFormattedNumber = formatPhoneNumber(country, phoneNumber);
            return {
                ...state,
                formattedPhoneNumber: newFormattedNumber,
                nationalNumber: phoneNumber,
                value: `+${getCountryCallingCode(country)}${phoneNumber}`,
            };
        }
        default:
            return state;
    }
};

/**
 * @param {import('@mui/material').BoxProps & { country: PhoneCountryOption }} props
 */
const CountryCodeMenuItem = props => {
    const { country, label, ...remain } = props;
    return (
        <Box display="flex" alignItems="center" gap={1} {...remain}>
            <img
                src={country.flagSrc}
                alt={country.label}
                style={{ width: 20, height: 15 }}
                onError={e => {
                    e.target.style.display = 'none';
                }}
            />
            <span>{label}</span>
        </Box>
    );
};

CountryCodeMenuItem.propTypes = {
    country: PropTypes.shape({
        country: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        callingCode: PropTypes.string.isRequired,
        flagSrc: PropTypes.string.isRequired,
    }).isRequired,
    label: PropTypes.string,
};

/**
 * @typedef {Object} Base
 * @property {Record<string,any>=} translationOptions The `options` object provided to all internal <Locale/> translations for the PhoneNumber
 *
 * @typedef {import('@mui/material').TextFieldProps & Base} PhoneNumberProps
 */

/**
 * @type {React.ForwardRefExoticComponent<PhoneNumberProps>}
 */
export const PhoneNumber = React.forwardRef((props, ref) => {
    const { defaultValue, disabled, label, placeholder, value, ...remain } = props;
    const formControlRef = useRef(null);
    const inputRef = useRef(null);
    const [state, dispatch, getState] = useThunkReducer(reducer, props, initState);

    const handleCountryChange = useBoundCallback(
        (onChange, event) => {
            const countryCode = event.target.value;
            dispatch({ type: 'SET_COUNTRY', payload: countryCode });
            // Using `getState` here gives the latest state after the dispatch
            const { value } = getState();
            onChange && onChange(value);
        },
        [props.onChange]
    );

    const handlePhoneNumberChange = useBoundCallback(
        (onChange, event) => {
            const { country } = getState();
            const input = event.target;
            const currentCursorPos = input.selectionStart;
            const inputValue = input.value;
            const rawPosition = countDigitsBefore(inputValue, currentCursorPos);
            if (!inputValue) {
                dispatch({ type: 'SET_PHONE_NUMBER', payload: '' });
            } else {
                const numericOnly = inputValue.replace(/\D/g, '');
                dispatch({
                    type: 'SET_PHONE_NUMBER',
                    payload: getNationalNumber(country, numericOnly),
                });
            }

            // Using `getState` here gives the latest state after the dispatch
            const { formattedPhoneNumber, value } = getState();
            const targetDigitPosition = findCursorPosition(formattedPhoneNumber, rawPosition);
            if (targetDigitPosition) {
                // Update cursor position
                setTimeout(() => {
                    if (inputRef.current) {
                        inputRef.current.setSelectionRange(
                            targetDigitPosition,
                            targetDigitPosition
                        );
                    }
                }, 0);
            }
            onChange && onChange(value);
        },
        [props.onChange]
    );

    const [focused, setFocus] = useState(false);
    const handleFocus = useBoundCallback(
        (onFocus, fieldName, /** @type {import('react').FocusEvent} */ event) => {
            const currentFocusedItem = event.currentTarget.closest(`.MuiFormControl-root`);
            // Do not change focus if the target is a child of the input
            if (currentFocusedItem === formControlRef.current) {
                setFocus(true);
            } else {
                setFocus(false);
                // We have to update the event target with the field name and value for Formik
                event.target = { name: fieldName, value: getState().value };
                onFocus && onFocus(event);
            }
        },
        [props.onFocus, props.name]
    );

    const handleBlur = useBoundCallback(
        (onBlur, fieldName, event) => {
            const currentFocusedItem = event.relatedTarget
                ? event.relatedTarget.closest(`.MuiFormControl-root`)
                : document.activeElement;
            if (currentFocusedItem === formControlRef.current) {
                setFocus(true);
            } else {
                setFocus(false);
                // We have to update the event target with the field name and value for Formik
                event.target = { name: fieldName, value: getState().value };
                onBlur && onBlur(event);
            }
        },
        [props.onBlur, props.name]
    );

    const memoFormControlProps = React.useMemo(
        () => ({
            ref: formControlRef,
            focused,
        }),
        [focused]
    );

    return (
        <TextField
            {...remain}
            FormControlProps={memoFormControlProps}
            ref={ref}
            label={label}
            value={state.formattedPhoneNumber}
            disabled={disabled}
            onChange={handlePhoneNumberChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder || state.placeholder}
            inputProps={{
                ...props.inputProps,
                type: 'tel',
            }}
            InputProps={{
                ...props.InputProps,
                inputRef,
                sx: numberInputStyles,
                startAdornment: (
                    <InputAdornment position="start" sx={{ ml: '-1em' }}>
                        <Select
                            value={state.country || ''}
                            onChange={handleCountryChange}
                            variant="outlined"
                            label={label}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            sx={countrySelectStyles}
                            notched
                            disabled={disabled}
                            size="small"
                            renderValue={value => {
                                const country = PHONE_COUNTRY_OPTIONS.find(
                                    option => option.country === value
                                );
                                return country ? (
                                    <CountryCodeMenuItem
                                        country={country}
                                        label={`+${country.callingCode}`}
                                    />
                                ) : null;
                            }}
                        >
                            {PHONE_COUNTRY_OPTIONS.map(country => (
                                <MenuItem key={country.country} value={country.country}>
                                    <CountryCodeMenuItem
                                        country={country}
                                        label={`${country.label} (+${country.callingCode})`}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </InputAdornment>
                ),
            }}
        />
    );
});

PhoneNumber.propTypes = {
    'aria-describedby': PropTypes.string,
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    fullWidth: PropTypes.bool,
    id: PropTypes.string,
    InputLabelProps: PropTypes.object,
    InputProps: PropTypes.object,
    inputProps: PropTypes.object,
    label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    placeholder: PropTypes.string,
    value: PropTypes.any,
};

export default PhoneNumber;

export const FormikPhoneNumber = withFormikField(PhoneNumber);
FormikPhoneNumber.displayName = 'FormikPhoneNumber';
